import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'
import { supabase } from './supabase'
import { isNative, isIOS } from './platform'

/**
 * Initialize Google Auth plugin for native platforms.
 * Must be called before using Google sign-in on native.
 */
export async function initGoogleAuth() {
  if (!isNative()) return

  try {
    await GoogleAuth.initialize()
  } catch (error) {
    console.error('Google Auth initialization failed:', error)
    throw error
  }
}

/**
 * Sign in with Google using native authentication flow.
 * Returns the Supabase session on success.
 *
 * On native: uses Capacitor GoogleAuth plugin to get id_token,
 * then exchanges it with Supabase signInWithIdToken.
 *
 * On web: throws error (use supabase.auth.signInWithOAuth instead).
 */
export async function signInWithGoogleNative() {
  if (!isNative()) {
    throw new Error('signInWithGoogleNative() is only for native platforms. Use supabase.auth.signInWithOAuth() on web.')
  }

  try {
    // Get Google credentials from native plugin
    const googleUser = await GoogleAuth.signIn()

    if (!googleUser?.authentication?.idToken) {
      throw new Error('No ID token received from Google sign-in')
    }

    // Exchange the Google ID token with Supabase
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: googleUser.authentication.idToken,
    })

    if (error) throw error

    return data
  } catch (error) {
    console.error('Native Google sign-in failed:', error)
    throw error
  }
}

/**
 * Sign out from Google on native platforms.
 */
export async function signOutGoogleNative() {
  if (!isNative()) return

  try {
    await GoogleAuth.signOut()
  } catch (error) {
    console.error('Google sign-out failed:', error)
    // Don't throw - sign-out failure shouldn't block the app
  }
}

/**
 * Generate a cryptographically-random hex string.
 */
function generateRandomHex(byteLength) {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength))
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * SHA-256 hash a string (returns hex).
 */
async function sha256(input) {
  const encoder = new TextEncoder()
  const data = encoder.encode(input)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Sign in with Apple using native authentication flow.
 * Only available on iOS.
 *
 * Returns the Supabase session on success.
 */
export async function signInWithAppleNative() {
  if (!isIOS()) {
    throw new Error('signInWithAppleNative() is only available on iOS')
  }

  try {
    // Generate fresh cryptographically-random state and nonce per sign-in attempt
    const rawNonce = generateRandomHex(32)
    const state = generateRandomHex(16)

    // Store state to verify on callback
    sessionStorage.setItem('apple_oauth_state', state)

    // Hash the nonce before sending to Apple (Apple requires SHA-256 hash)
    const hashedNonce = await sha256(rawNonce)

    const result = await SignInWithApple.authorize({
      clientId: 'io.fragranceos.app',
      redirectURI: 'https://fragrance-os.pages.dev/auth/callback',
      scopes: 'email name',
      state,
      nonce: hashedNonce,
    })

    if (!result.response?.identityToken) {
      throw new Error('No identity token received from Apple sign-in')
    }

    // Verify state matches to prevent CSRF
    const storedState = sessionStorage.getItem('apple_oauth_state')
    if (result.response.state !== storedState) {
      sessionStorage.removeItem('apple_oauth_state')
      throw new Error('State mismatch - possible CSRF attack')
    }

    // Clean up stored state
    sessionStorage.removeItem('apple_oauth_state')

    // Exchange the Apple ID token with Supabase, passing the raw nonce
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: result.response.identityToken,
      nonce: rawNonce,
    })

    if (error) throw error

    return data
  } catch (error) {
    console.error('Apple sign-in failed:', error)
    // Clean up on error
    sessionStorage.removeItem('apple_oauth_state')
    throw error
  }
}
